import React, { useState, useEffect } from 'react';
import { Expense, ExpenseType, ExpenseStatus, Property, Agency, UserProfile, Revenue } from '../types';
import { storage } from '../services/storage';
import { useModal } from './ui/ModalProvider';
import { AddExpenseModal } from './modals/AddExpenseModal';
import { AddRevenueModal } from './modals/AddRevenueModal';
import { ExpensesTableView } from './ExpensesTableView';
import { 
  Search, Filter, Plus, Euro, Calendar, Building, TrendingUp, Edit2, Trash2,
  ChevronDown, ChevronUp, Tag, CheckCircle2, Clock, XCircle, AlertCircle,
  Download, Copy, MoreVertical, Keyboard, X, Info, Sparkles, BarChart3
} from 'lucide-react';

interface ExpensesPanelProps {
  userProfile: UserProfile | null;
  selectedAgencyId?: string | null;
}

const EXPENSE_TYPE_LABELS: Record<ExpenseType, { label: string; color: string }> = {
  RENT: { label: 'Loyers', color: 'bg-purple-100 text-purple-800' },
  CHARGES: { label: 'Charges', color: 'bg-blue-100 text-blue-800' },
  CLEANING: { label: 'Ménage', color: 'bg-green-100 text-green-800' },
  MAINTENANCE: { label: 'Entretien', color: 'bg-orange-100 text-orange-800' },
  INSURANCE: { label: 'Assurance', color: 'bg-cyan-100 text-cyan-800' },
  UTILITIES: { label: 'Utilities', color: 'bg-indigo-100 text-indigo-800' },
  SOFTWARE: { label: 'Logiciels', color: 'bg-pink-100 text-pink-800' },
  PLACID_COMMISSION: { label: 'Commission Placid', color: 'bg-amber-100 text-amber-800' },
  OTHER: { label: 'Autres', color: 'bg-slate-100 text-slate-800' }
};


export const ExpensesPanel: React.FC<ExpensesPanelProps> = ({ userProfile, selectedAgencyId }) => {
  const { toast, confirm } = useModal();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [editingRevenue, setEditingRevenue] = useState<Revenue | null>(null);

  // Filtres
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState<number | null>(new Date().getMonth() + 1);
  const [propertyFilter, setPropertyFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<ExpenseType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list'); // Vue liste ou tableau

  // Stats
  const [stats, setStats] = useState({
    totalExpenses: 0,
    totalByType: {} as Record<ExpenseType, number>
  });

  // UX Enhancements
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [previousMonthStats, setPreviousMonthStats] = useState<{expenses: number, revenues: number} | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    loadProperties();
  }, [selectedAgencyId]);

  useEffect(() => {
    if (properties.length > 0 || propertyFilter === 'all') {
      loadData();
    }
  }, [year, month, propertyFilter, typeFilter, selectedAgencyId, properties.length]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input/textarea/select
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return;
      }

      // Ignore if modal is open
      if (isExpenseModalOpen || isRevenueModalOpen) {
        if (e.key === 'Escape') {
          setIsExpenseModalOpen(false);
          setIsRevenueModalOpen(false);
          setEditingExpense(null);
          setEditingRevenue(null);
        }
        return;
      }

      // Shortcuts
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setEditingExpense(null);
        setIsExpenseModalOpen(true);
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        setEditingRevenue(null);
        setIsRevenueModalOpen(true);
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          handleExportCSV();
        } else {
          setShowExportMenu(!showExportMenu);
        }
      } else if (e.key === '?') {
        e.preventDefault();
        setShowKeyboardShortcuts(!showKeyboardShortcuts);
      } else if (e.key === 'Escape') {
        setShowKeyboardShortcuts(false);
        setShowExportMenu(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpenseModalOpen, isRevenueModalOpen, showExportMenu]);

  // Load previous month stats for comparison
  useEffect(() => {
    const loadPreviousMonthStats = async () => {
      try {
        const prevMonth = month === 1 ? 12 : (month || 1) - 1;
        const prevYear = month === 1 ? year - 1 : year;
        
        const prevFilters: any = {
          agencyId: selectedAgencyId || undefined,
          year: prevYear,
          month: prevMonth
        };
        
        const [prevExpenses, prevRevenues] = await Promise.all([
          storage.getExpenses(prevFilters),
          propertyFilter !== 'all' 
            ? storage.getRevenue(propertyFilter, prevYear, prevMonth)
            : Promise.resolve([])
        ]);

        const prevTotalExpenses = prevExpenses.reduce((sum, e) => sum + e.amount, 0);
        const prevTotalRevenues = prevRevenues.reduce((sum, r) => sum + (r.netRevenue || 0), 0);
        
        setPreviousMonthStats({ expenses: prevTotalExpenses, revenues: prevTotalRevenues });
      } catch (error) {
        // Ignore errors
      }
    };

    if (month && year) {
      loadPreviousMonthStats();
    }
  }, [month, year, propertyFilter, selectedAgencyId]);

  const loadProperties = async () => {
    const props = await storage.getProperties(selectedAgencyId || undefined);
    setProperties(props.filter(p => p.status === 'MANAGED'));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const filters: any = {
        agencyId: selectedAgencyId || undefined,
        year,
        month: month || undefined,
        expenseType: typeFilter !== 'all' ? typeFilter : undefined
      };

      if (propertyFilter !== 'all') {
        filters.propertyId = propertyFilter;
      }

      // Charger dépenses et revenus en parallèle
      const [expensesData, revenuesData] = await Promise.all([
        storage.getExpenses(filters),
        propertyFilter !== 'all' 
          ? storage.getRevenue(propertyFilter, year, month || undefined)
          : (async () => {
              // Si pas de filtre par bien, charger pour tous les biens de l'agence
              const allRevenues: Revenue[] = [];
              for (const prop of properties) {
                if (prop.id) {
                  try {
                    const propRevenues = await storage.getRevenue(prop.id, year, month || undefined);
                    allRevenues.push(...propRevenues);
                  } catch (err) {
                    // Ignore les erreurs
                  }
                }
              }
              return allRevenues;
            })()
      ]);
      
      // Filtrer par recherche textuelle
      let filtered = expensesData;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = expensesData.filter(e => 
          e.category.toLowerCase().includes(query) ||
          e.description?.toLowerCase().includes(query) ||
          e.supplierName?.toLowerCase().includes(query)
        );
      }

      setExpenses(filtered);
      setRevenues(revenuesData);

      // Calculer stats
      const totalExpenses = filtered.reduce((sum, e) => sum + e.amount, 0);
      const totalByType: Record<ExpenseType, number> = {
        RENT: 0, CHARGES: 0, CLEANING: 0, MAINTENANCE: 0,
        INSURANCE: 0, UTILITIES: 0, SOFTWARE: 0, PLACID_COMMISSION: 0, OTHER: 0
      };

      filtered.forEach(e => {
        totalByType[e.expenseType] = (totalByType[e.expenseType] || 0) + e.amount;
      });

      setStats({ totalExpenses, totalByType });
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExpense = async (expense: Expense) => {
    await storage.saveExpense(expense);
    await loadData();
  };

  const handleSaveRevenue = async (revenue: Revenue) => {
    await storage.saveRevenue(revenue);
    await loadData();
  };

  const handleDeleteExpense = async (id: string) => {
    const confirmed = await confirm('Supprimer cette dépense ?', 'Cette action est irréversible.');
    if (!confirmed) return;

    const { error } = await storage.deleteExpense(id);
    if (error) {
      toast.error('Erreur lors de la suppression');
    } else {
      toast.success('Dépense supprimée');
      await loadData();
    }
  };

  // Quick actions
  const handleDuplicateExpense = async (expense: Expense) => {
    const nextMonth = expense.month === 12 ? 1 : expense.month + 1;
    const nextYear = expense.month === 12 ? expense.year + 1 : expense.year;
    
    const duplicatedExpense: Expense = {
      ...expense,
      id: undefined,
      year: nextYear,
      month: nextMonth,
    };

    await handleSaveExpense(duplicatedExpense);
    toast.success('Dépense dupliquée pour le mois suivant');
  };


  // Export functions
  const handleExportCSV = () => {
    const csvRows: string[] = [];
    
    // Headers
    csvRows.push('Type,Période,Bien,Type de dépense,Catégorie,Description,Montant');
    
    // Expenses
    expenses.forEach(expense => {
      const property = properties.find(p => p.id === expense.propertyId);
      const period = expense.isRecurring && expense.month === null 
        ? 'Tous les mois (récurrent)'
        : `${months.find(m => m.value === expense.month)?.label} ${expense.year}`;
      csvRows.push([
        'Dépense',
        period,
        property ? (property.name || property.address) : '—',
        EXPENSE_TYPE_LABELS[expense.expenseType].label,
        expense.category,
        expense.description || '',
        expense.amount.toFixed(2)
      ].map(field => `"${field}"`).join(','));
    });
    
    // Revenues
    revenues.forEach(revenue => {
      const property = properties.find(p => p.id === revenue.propertyId);
      csvRows.push([
        'Revenu',
        `${months.find(m => m.value === revenue.month)?.label} ${revenue.year}`,
        property ? (property.name || property.address) : '—',
        '',
        '',
        '',
        revenue.netRevenue?.toFixed(2) || '0.00',
        ''
      ].map(field => `"${field}"`).join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `finances_${year}_${month || 'all'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Export CSV téléchargé');
    setShowExportMenu(false);
  };

  // Quick filters
  const handleQuickFilter = (filterType: string) => {
    if (activeQuickFilter === filterType) {
      // Reset filter
      setActiveQuickFilter(null);
      setYear(new Date().getFullYear());
      setMonth(new Date().getMonth() + 1);
      setStatusFilter('all');
      setPropertyFilter('all');
      setTypeFilter('all');
    } else {
      setActiveQuickFilter(filterType);
      const now = new Date();
      
      switch (filterType) {
        case 'this-month':
          setYear(now.getFullYear());
          setMonth(now.getMonth() + 1);
          setStatusFilter('all');
          break;
        case 'last-month':
          const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
          const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
          setYear(prevYear);
          setMonth(prevMonth);
          setStatusFilter('all');
          break;
      }
    }
  };

  const months = [
    { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' }
  ];

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div className="space-y-6">
      {/* Stats skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 border border-slate-200 animate-pulse">
            <div className="h-3 bg-slate-200 rounded w-20 mb-3"></div>
            <div className="h-7 bg-slate-200 rounded w-24"></div>
          </div>
        ))}
      </div>
      
      {/* Table skeleton */}
      {[...Array(2)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
            <div className="h-4 bg-slate-200 rounded w-32 animate-pulse"></div>
          </div>
          <div className="p-5 space-y-3">
            {[...Array(5)].map((_, j) => (
              <div key={j} className="flex items-center gap-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded flex-1"></div>
                <div className="h-4 bg-slate-200 rounded flex-1"></div>
                <div className="h-4 bg-slate-200 rounded flex-1"></div>
                <div className="h-4 bg-slate-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading && expenses.length === 0) {
    return (
      <div className="p-6 h-full overflow-y-auto bg-slate-50/50">
        <SkeletonLoader />
      </div>
    );
  }

  const totalRevenues = revenues.reduce((sum, r) => sum + (r.netRevenue || 0), 0);
  const netBalance = totalRevenues - stats.totalExpenses;

  return (
    <div className="p-6 h-full overflow-y-auto bg-slate-50/50 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mr-3 border border-amber-100">
              <Euro className="w-5 h-5 text-amber-600" />
            </div>
            Finances
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">Pilotez votre rentabilité et vos flux financiers</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl shadow-sm transition-all font-semibold text-sm gap-2"
              title="Exporter (E)"
            >
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export</span>
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 min-w-[180px]">
                  <button
                    onClick={handleExportCSV}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
              </>
            )}
          </div>
          
          <button
            onClick={() => { setEditingRevenue(null); setIsRevenueModalOpen(true); }}
            className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl shadow-sm transition-all font-semibold text-sm gap-2"
            title="Nouveau revenu (R)"
          >
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="hidden md:inline">Revenu</span>
          </button>
          <button
            onClick={() => { setEditingExpense(null); setIsExpenseModalOpen(true); }}
            className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 bg-slate-900 text-white hover:bg-slate-800 rounded-xl shadow-md shadow-slate-200 transition-all font-semibold text-sm gap-2"
            title="Nouvelle dépense (N)"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">Dépense</span>
          </button>
          
          {/* Keyboard shortcuts help button */}
          <button
            onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
            title="Raccourcis clavier (?)"
          >
            <Keyboard className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'this-month', label: '📅 Ce mois', icon: Calendar },
          { id: 'last-month', label: '📆 Mois précédent', icon: Calendar }
        ].map(filter => (
          <button
            key={filter.id}
            onClick={() => handleQuickFilter(filter.id)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              activeQuickFilter === filter.id
                ? 'bg-amber-500 text-white shadow-md'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-amber-300'
            }`}
          >
            <filter.icon className="w-3 h-3" />
            {filter.label}
          </button>
        ))}
      </div>

      {/* Alerts & Insights */}
      {(() => {
        const revenueChange = previousMonthStats 
          ? ((totalRevenues - previousMonthStats.revenues) / previousMonthStats.revenues) * 100 
          : null;
        const expenseChange = previousMonthStats 
          ? ((stats.totalExpenses - previousMonthStats.expenses) / previousMonthStats.expenses) * 100 
          : null;
        
        const insights = [];
        
        if (revenueChange && revenueChange > 15) {
          insights.push({
            type: 'success',
            icon: TrendingUp,
            message: `+${revenueChange.toFixed(0)}% de revenus vs mois précédent`,
            color: 'green'
          });
        }
        
        if (netBalance > 0 && totalRevenues > 0) {
          insights.push({
            type: 'info',
            icon: Sparkles,
            message: `Bénéfice record : ${netBalance.toFixed(2)} €`,
            color: 'blue'
          });
        }
        
        return insights.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {insights.map((insight, idx) => {
              const colorClasses = {
                amber: 'bg-amber-50 border-amber-200 text-amber-700',
                green: 'bg-green-50 border-green-200 text-green-700',
                blue: 'bg-blue-50 border-blue-200 text-blue-700'
              };
              
              return (
                <div
                  key={idx}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border ${colorClasses[insight.color as keyof typeof colorClasses] || 'bg-slate-50 border-slate-200 text-slate-700'}`}
                >
                  <insight.icon className="w-4 h-4" />
                  {insight.message}
                </div>
              );
            })}
          </div>
        ) : null;
      })()}

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Revenus Nets', val: totalRevenues, color: 'text-green-600', icon: TrendingUp },
          { label: 'Dépenses', val: stats.totalExpenses, color: 'text-red-600', icon: Euro },
          { label: 'Solde Net', val: netBalance, color: netBalance >= 0 ? 'text-green-600' : 'text-red-600', highlight: true }
        ].map((item, i) => (
          <div key={i} className={`bg-white rounded-2xl p-4 border border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${item.highlight ? 'ring-2 ring-slate-900 ring-offset-2' : ''}`}>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex justify-between items-center">
              {item.label}
              {item.icon && <item.icon className="w-3 h-3 opacity-50" />}
            </div>
            <div className={`text-xl font-bold truncate ${item.color}`}>
              {item.val.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
            </div>
          </div>
        ))}
      </div>

      {/* Simple Charts - Revenue vs Expenses Bar Chart */}
      {expenses.length > 0 || revenues.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Bar Chart: Revenus vs Dépenses */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              Comparaison Revenus / Dépenses
            </h3>
            <div className="flex items-end gap-4 h-48">
              <div className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex flex-col justify-end h-full gap-1" style={{ minHeight: '150px' }}>
                  {totalRevenues > 0 && (
                    <div 
                      className="bg-green-500 rounded-t transition-all hover:bg-green-600 cursor-pointer group relative"
                      style={{ height: `${Math.min(100, (totalRevenues / Math.max(totalRevenues, stats.totalExpenses)) * 100)}%` }}
                      title={`Revenus: ${totalRevenues.toFixed(2)} €`}
                    >
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {totalRevenues.toFixed(2)} €
                      </div>
                    </div>
                  )}
                  {stats.totalExpenses > 0 && (
                    <div 
                      className="bg-red-500 rounded-t transition-all hover:bg-red-600 cursor-pointer group relative"
                      style={{ height: `${Math.min(100, (stats.totalExpenses / Math.max(totalRevenues, stats.totalExpenses)) * 100)}%` }}
                      title={`Dépenses: ${stats.totalExpenses.toFixed(2)} €`}
                    >
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {stats.totalExpenses.toFixed(2)} €
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-xs font-semibold text-slate-600">Total</div>
              </div>
            </div>
            <div className="flex gap-4 mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-500"></div>
                <span className="text-xs text-slate-600">Revenus</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-500"></div>
                <span className="text-xs text-slate-600">Dépenses</span>
              </div>
            </div>
          </div>

          {/* Pie Chart: Répartition des dépenses par type */}
          {expenses.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Euro className="w-4 h-4 text-red-500" />
                Répartition par type
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {Object.entries(stats.totalByType)
                  .filter(([_, amount]) => amount > 0)
                  .sort(([_, a], [__, b]) => b - a)
                  .map(([type, amount]) => {
                    const percentage = (amount / stats.totalExpenses) * 100;
                    const typeInfo = EXPENSE_TYPE_LABELS[type as ExpenseType];
                    
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-slate-700">{typeInfo.label}</span>
                          <span className="text-slate-500">{percentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                          <div 
                            className={`h-full ${typeInfo.color.split(' ')[0]} rounded-full transition-all`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-slate-500 text-right">{amount.toFixed(2)} €</div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Vue Toggle et Filtres */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
            <Search className="w-4 h-4" />
            Filtres & Recherche
          </div>
          
          {/* Toggle vue tableau (seulement si un bien est sélectionné) */}
          {propertyFilter !== 'all' && (
            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Liste
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <BarChart3 className="w-3 h-3 inline mr-1" />
                Tableau
              </button>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-500 uppercase ml-1">Période</label>
            <div className="flex gap-2">
              <input type="number" min="2020" max="2100" className="w-full h-10 px-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-medium" value={year} onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())} />
              <select className="w-full h-10 px-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm appearance-none bg-no-repeat bg-[right_0.5rem_center]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0\' height=\'20\' width=\'20\'%3E%3Cpath stroke=\'%2364748b\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'m6 8 4 4 4-4\'/%3E%3C/svg%3E")'}} value={month || ''} onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">Mois</option>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-500 uppercase ml-1">Bien Immobilier</label>
            <select className="w-full h-10 px-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm" value={propertyFilter} onChange={(e) => {
              setPropertyFilter(e.target.value);
              // Si on sélectionne "all", revenir à la vue liste
              if (e.target.value === 'all') {
                setViewMode('list');
              }
            }}>
              <option value="all">Tous les biens</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-500 uppercase ml-1">Catégorie</label>
            <select className="w-full h-10 px-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as ExpenseType | 'all')}>
              <option value="all">Tous types</option>
              {Object.entries(EXPENSE_TYPE_LABELS).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="lg:col-span-3 space-y-1.5">
            <label className="text-[11px] font-bold text-slate-500 uppercase ml-1">Recherche textuelle</label>
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-amber-500 transition-colors" />
              <input type="text" className="w-full h-10 pl-10 pr-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm transition-all" placeholder="Rechercher une opération..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Vue Tableau (uniquement si un bien est sélectionné) */}
      {viewMode === 'table' && propertyFilter !== 'all' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          {(() => {
            const selectedProperty = properties.find(p => p.id === propertyFilter);
            if (!selectedProperty) return null;
            return (
              <ExpensesTableView
                propertyId={propertyFilter}
                property={selectedProperty}
                year={year}
                agencyId={selectedAgencyId || undefined}
                onExpenseSaved={loadData}
                onRevenueSaved={loadData}
              />
            );
          })()}
        </div>
      )}

      {/* Vue Liste */}
      {viewMode === 'list' && (
      <div className="space-y-6">
        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {expenses.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-red-50/50">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Euro className="w-4 h-4 text-red-600" />
                  Dépenses ({expenses.length})
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {expenses.map((expense) => {
                  const property = properties.find(p => p.id === expense.propertyId);
                  const typeInfo = EXPENSE_TYPE_LABELS[expense.expenseType];

                  return (
                    <div key={expense.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-md ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                            {expense.isRecurring && expense.month === null && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md text-[10px] font-bold text-blue-700">
                                <Tag className="w-3 h-3" />
                                Récurrent
                              </span>
                            )}
                          </div>
                          <div className="font-semibold text-slate-900 text-sm mb-1">{expense.category}</div>
                          {expense.description && (
                            <div className="text-xs text-slate-500 line-clamp-1">{expense.description}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-slate-900 text-lg">{expense.amount.toFixed(2)} €</div>
                          <div className="text-xs text-slate-500 mt-1">
                            {expense.isRecurring && expense.month === null ? (
                              'Tous les mois'
                            ) : (
                              <>
                                {months.find(m => m.value === expense.month)?.label} {expense.year}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <button onClick={() => handleDuplicateExpense(expense)} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors flex items-center gap-1">
                          <Copy className="w-3 h-3" />
                          Dupliquer
                        </button>
                        <button onClick={() => { setEditingExpense(expense); setIsExpenseModalOpen(true); }} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors flex items-center gap-1">
                          <Edit2 className="w-3 h-3" />
                          Modifier
                        </button>
                        {!expense.isPlacidCommission && (
                          <button onClick={() => handleDeleteExpense(expense.id!)} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {revenues.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-green-50/50">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Revenus ({revenues.length})
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {revenues.map((revenue) => {
                  const property = properties.find(p => p.id === revenue.propertyId);
                  return (
                    <div key={revenue.id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="font-semibold text-slate-900 text-sm mb-1">
                            {property?.name || property?.address || '—'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {months.find(m => m.value === revenue.month)?.label} {revenue.year}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600 text-lg">{revenue.netRevenue?.toFixed(2) || '0.00'} €</div>
                          <div className="text-xs text-slate-500 mt-1">Nets</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3 pt-3 border-t border-slate-100">
                        <div>
                          <span className="text-slate-500">Brut:</span> {revenue.grossRevenue?.toFixed(2) || '0.00'} €
                        </div>
                        <div>
                          <span className="text-slate-500">Commissions:</span> -{revenue.totalCommissions?.toFixed(2) || '0.00'} €
                        </div>
                      </div>
                      <button 
                        onClick={() => { setEditingRevenue(revenue); setIsRevenueModalOpen(true); }} 
                        className="w-full px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        Modifier
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block space-y-6">
          {/* Table Sections */}
          {[
          { 
            title: 'Revenus', 
            icon: TrendingUp, 
            data: revenues, 
            bg: 'bg-green-50/50', 
            iconColor: 'text-green-600',
            columns: ['Période', 'Propriété', 'Brut', 'Comms', 'Net', '']
          },
          { 
            title: 'Dépenses', 
            icon: Euro, 
            data: expenses, 
            bg: 'bg-slate-50/50', 
            iconColor: 'text-red-600',
            columns: ['Période', 'Bien', 'Type / Catégorie', 'Détails', 'Montant', '']
          }
        ].map((section, idx) => (
          (section.data.length > 0 || idx === 1) && (
            <div key={idx} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className={`px-5 py-4 flex items-center justify-between border-b border-slate-100 ${section.bg}`}>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <section.icon className={`w-4 h-4 ${section.iconColor}`} />
                  {section.title} <span className="text-slate-400 font-medium ml-1">({section.data.length})</span>
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/50">
                      {section.columns.map((col, cidx) => (
                        <th key={cidx} className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {section.data.length === 0 ? (
                      <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-400 text-sm">Aucune donnée correspondante</td></tr>
                    ) : (
                      section.data.map((item: any) => {
                        const property = properties.find(p => p.id === item.propertyId);
                        if (section.title === 'Revenus') {
                          return (
                            <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                              <td className="px-5 py-3.5 text-sm font-semibold text-slate-700 whitespace-nowrap">
                                {months.find(m => m.value === item.month)?.label} {item.year}
                              </td>
                              <td className="px-5 py-3.5 text-sm text-slate-600 font-medium truncate max-w-[200px]">
                                {property?.name || property?.address || '—'}
                              </td>
                              <td className="px-5 py-3.5 text-sm font-medium text-slate-600 whitespace-nowrap">
                                {item.grossRevenue?.toFixed(2)} €
                              </td>
                              <td className="px-5 py-3.5 text-sm font-medium text-red-500 whitespace-nowrap">
                                -{item.totalCommissions?.toFixed(2)} €
                              </td>
                              <td className="px-5 py-3.5 text-sm font-bold text-green-600 whitespace-nowrap">
                                {item.netRevenue?.toFixed(2)} €
                              </td>
                              <td className="px-5 py-3.5 text-right">
                                <button onClick={() => { setEditingRevenue(item); setIsRevenueModalOpen(true); }} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        } else {
                          const typeInfo = EXPENSE_TYPE_LABELS[item.expenseType];
                          return (
                            <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                              <td className="px-5 py-3.5 text-sm font-semibold text-slate-700 whitespace-nowrap">
                                {item.isRecurring && item.month === null ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md text-[10px] font-bold text-blue-700">
                                      <Tag className="w-3 h-3" />
                                      Récurrent
                                    </span>
                                    <span className="text-slate-500 text-xs">Tous les mois</span>
                                  </div>
                                ) : (
                                  <>
                                    {months.find(m => m.value === item.month)?.label} {item.year}
                                  </>
                                )}
                              </td>
                              <td className="px-5 py-3.5 text-sm text-slate-600 font-medium max-w-[150px] truncate">
                                {property ? (property.name || property.address) : (item.isSoftwareExpense ? 'Logiciels' : item.isPlacidCommission ? 'Commission' : '—')}
                              </td>
                              <td className="px-5 py-3.5">
                                <div className="flex flex-col">
                                  <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-md w-fit mb-1 ${typeInfo.color}`}>
                                    {typeInfo.label}
                                  </span>
                                  <span className="text-xs font-semibold text-slate-900">{item.category}</span>
                                </div>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className="text-xs text-slate-500 line-clamp-1">{item.description || '—'}</span>
                              </td>
                              <td className="px-5 py-3.5 text-sm font-bold text-slate-900 whitespace-nowrap">
                                {item.amount.toFixed(2)} €
                              </td>
                              <td className="px-5 py-3.5 text-right">
                                <div className="relative">
                                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button 
                                      onClick={() => handleDuplicateExpense(item)} 
                                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                                      title="Dupliquer"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => { setEditingExpense(item); setIsExpenseModalOpen(true); }} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all" title="Modifier">
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    {!item.isPlacidCommission && (
                                      <button 
                                        onClick={() => handleDeleteExpense(item.id!)} 
                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                        title="Supprimer"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ))}
        </div>
      </div>
      )}

      {/* Keyboard Shortcuts Overlay */}
      {showKeyboardShortcuts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowKeyboardShortcuts(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-amber-500" />
                Raccourcis clavier
              </h3>
              <button onClick={() => setShowKeyboardShortcuts(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <div className="space-y-3">
              {[
                { key: 'N', description: 'Nouvelle dépense', icon: Plus },
                { key: 'R', description: 'Nouveau revenu', icon: TrendingUp },
                { key: 'E', description: 'Ouvrir le menu Export', icon: Download },
                { key: 'Esc', description: 'Fermer les modales / menus', icon: X },
                { key: '?', description: 'Afficher cette aide', icon: Info }
              ].map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <shortcut.icon className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700 font-medium">{shortcut.description}</span>
                  </div>
                  <kbd className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-700 shadow-sm">
                    {shortcut.key}
                  </kbd>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                Appuyez sur <kbd className="px-2 py-0.5 bg-slate-100 rounded text-xs font-bold">Esc</kbd> ou cliquez en dehors pour fermer
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {isRevenueModalOpen && (
        <AddRevenueModal
          isOpen={isRevenueModalOpen}
          onClose={() => {
            setIsRevenueModalOpen(false);
            setEditingRevenue(null);
          }}
          onSave={handleSaveRevenue}
          revenueToEdit={editingRevenue}
          properties={properties}
          agencyId={selectedAgencyId || undefined}
          year={year}
          month={month || undefined}
        />
      )}

      {isExpenseModalOpen && (
        <AddExpenseModal
          isOpen={isExpenseModalOpen}
          onClose={() => {
            setIsExpenseModalOpen(false);
            setEditingExpense(null);
          }}
          onSave={handleSaveExpense}
          propertyId={propertyFilter !== 'all' ? propertyFilter : undefined}
          properties={properties}
          agencyId={selectedAgencyId || undefined}
          year={year}
          month={month || undefined}
          expenseToEdit={editingExpense}
        />
      )}
    </div>
  );
};
